/**
 * The presence token (architecture §9): proof, minted by the api after a passkey assertion, that
 * a person is at this browser. It lives in memory only: never localStorage, sessionStorage or
 * IndexedDB, so it dies with the tab and nothing on disk can be lifted to approve a request.
 */
export interface PresenceStore {
  /** The token while it is valid (with a small margin before expiry), otherwise null. */
  token(): string | null;
  /** When the current token stops working, or null when there is none. */
  expiresAt(): Date | null;
  set(token: string, expiresAt: Date): void;
  clear(): void;
  /** For `useSyncExternalStore`: called whenever the token is set or cleared. */
  subscribe(listener: () => void): () => void;
}

/** A token this close to expiry is treated as gone, so a request does not race the deadline. */
export const EXPIRY_MARGIN_MS = 30_000;

export function createPresenceStore(now: () => number = Date.now): PresenceStore {
  let current: { token: string; expiresAt: Date } | null = null;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((l) => l());
  const valid = () => current !== null && current.expiresAt.getTime() - EXPIRY_MARGIN_MS > now();

  return {
    token: () => (valid() && current !== null ? current.token : null),
    expiresAt: () => (valid() && current !== null ? current.expiresAt : null),
    set(token, expiresAt) {
      current = { token, expiresAt };
      notify();
    },
    clear() {
      current = null;
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** The app's one store; tests make their own with `createPresenceStore`. */
export const presenceStore = createPresenceStore();

/** `x-kwp-presence` when a valid token exists; nothing otherwise. */
export function presenceHeaders(store: PresenceStore): Record<string, string> {
  const token = store.token();
  return token === null ? {} : { "x-kwp-presence": token };
}
